/**
 * API Service Layer
 * Axios instance with Telegram initData authentication
 *
 * IMPORTANT: Types match actual backend responses.
 * See FRONTEND_API_CONTRACTS.md for documentation.
 */

import axios, { AxiosInstance, AxiosError } from 'axios';
import type {
  ApiResponse,
  // Wallet
  PendingDepositResponse,
  DepositInitResponse,
  DepositStatusResponse,
  DepositCheckResponse,
  DepositReceiptResponse,
  WithdrawalInitResponse,
  WalletBonusResponse,
  DepositRequest,
  ReceiptRequest,
  WithdrawalRequest,
  // Media (from shared contracts)
  MediaUploadType,
  UploadSuccessResponse,
  // Admin
  DashboardResponse,
  UsersListResponse,
  UserDetailResponse,
  UserTransactionsResponse,
  RecreateUserResponse,
  DepositsListResponse,
  DepositDetailResponse,
  ForceCompleteResponse,
  SyncDepositResponse,
  WithdrawalsListResponse,
  ResendWithdrawalResponse,
  AdminWithdrawalDetailResponseData,
  EditWithdrawalResponseData,
  EditDepositResponseData,
  TurnoverReportResponse,
  CascadeConfigResponse,
  DisabledMethodsResponse,
  BonusConfigResponse,
  LimitsResponse,
  MethodLimits,
  ForceCompleteRequest,
  SwapPositionsRequest,
  ReorderCascadeRequest,
  DropFromCascadeRequest,
  AddToCascadeRequest,
  BonusUpdateRequest,
  UpdateLimitsRequest,
  // Worker
  PendingWithdrawalsResponse,
  WithdrawalDetailResponse,
  LockWithdrawalResponse,
  AutoCascadeResponse,
  ManualCompleteResponse,
  DeclineWithdrawalResponse,
  EditAmountResponse,
  ConfirmCompleteResponse,
  EditAmountRequest,
  DeclineRequest,
  ManualCompleteRequest,
  // Referee
  RefereeProfileResponse,
  RefereeBalanceLogResponse,
  RefereeWithdrawalsResponse,
  CreateRefereeWithdrawalResponse,
  TokenConversionResponse,
  RefereeConversionsResponse,
  RefereeWithdrawalRequest,
  TokenConversionRequest,
} from '@/types';

// Referee types from shared contracts
import type { RefereeListItem } from '@club-bot/shared/contracts';

// =============================================================================
// Configuration
// =============================================================================

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// Granular test flags (replaces monolithic VITE_MINI_APP_ENV)
// VITE_SKIP_TELEGRAM_AUTH=true - Use fake initData instead of Telegram
// VITE_MOCK_USER_ID=7424583890 - Test user ID when auth is skipped
const SKIP_TELEGRAM_AUTH = import.meta.env.VITE_SKIP_TELEGRAM_AUTH === 'true' || import.meta.env.VITE_MINI_APP_ENV === 'test';
const MOCK_USER_ID = import.meta.env.VITE_MOCK_USER_ID || import.meta.env.VITE_TEST_USER_ID || '7424583890';

// =============================================================================
// Telegram Auth Helpers
// =============================================================================

/**
 * Generate fake initData for testing (when SKIP_TELEGRAM_AUTH=true)
 */
function generateFakeInitData(): string {
  const user = {
    id: parseInt(MOCK_USER_ID),
    first_name: 'Test',
    last_name: 'User',
    username: 'testuser',
    language_code: 'en',
  };
  const authDate = Math.floor(Date.now() / 1000);
  return `user=${encodeURIComponent(JSON.stringify(user))}&auth_date=${authDate}&hash=test_mode`;
}

/**
 * Get Telegram initData from WebApp or generate fake for testing
 */
function getTelegramInitData(): string {
  if (SKIP_TELEGRAM_AUTH) {
    return generateFakeInitData();
  }

  const tg = window.Telegram?.WebApp;
  if (!tg?.initData) {
    console.warn('[API] No Telegram initData found');
    return '';
  }
  return tg.initData;
}

/**
 * Get current user's Telegram ID
 */
export function getCurrentUserId(): string {
  if (SKIP_TELEGRAM_AUTH) {
    return MOCK_USER_ID;
  }
  return window.Telegram?.WebApp?.initDataUnsafe?.user?.id?.toString() || '';
}

// =============================================================================
// Axios Instance
// =============================================================================

const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds
});

// Add Telegram initData to all requests
api.interceptors.request.use((config) => {
  const initData = getTelegramInitData();
  if (initData) {
    config.headers['X-Telegram-Init-Data'] = initData;
  }
  return config;
});

// Global error handling
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiResponse<unknown>>) => {
    const message = error.response?.data?.error || error.message || 'Unknown error';
    console.error('[API Error]', message);
    return Promise.reject(error);
  }
);

// =============================================================================
// API Error Helper
// =============================================================================

export class ApiError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public data?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

// =============================================================================
// Wallet API (Main Bot - /wallet routes)
// =============================================================================

export const walletAPI = {
  /**
   * Get wallet status (includes minimum withdrawal requirement)
   */
  getStatus: () =>
    api.get<ApiResponse<{
      minimumWithdrawal: number;
      totalDeposit: number;
      totalBonus: number;
      totalWithdrawal: number;
      hasGameAccount: boolean;
    }>>('/api/wallet/status'),

  /**
   * Get current bonus percentage (polled every 60s on deposit page)
   */
  getBonus: () =>
    api.get<ApiResponse<WalletBonusResponse>>('/api/wallet/bonus'),

  /**
   * Check for pending deposit on app load
   */
  getPendingDeposit: () =>
    api.get<ApiResponse<PendingDepositResponse>>('/api/wallet/deposit/pending'),

  /**
   * Initiate a deposit
   */
  initiateDeposit: (data: DepositRequest) =>
    api.post<ApiResponse<DepositInitResponse>>('/api/wallet/deposit', data),

  /**
   * Get deposit status (polling)
   */
  getDepositStatus: (referenceNumber: string) =>
    api.get<ApiResponse<DepositStatusResponse>>(`/api/wallet/deposit/${referenceNumber}/status`),

  /**
   * Manual provider status check
   */
  checkDepositStatus: (referenceNumber: string) =>
    api.post<ApiResponse<DepositCheckResponse>>(`/api/wallet/deposit/${referenceNumber}/check`),

  /**
   * Submit receipt photo to complete deposit
   */
  submitReceipt: (referenceNumber: string, data: ReceiptRequest) =>
    api.post<ApiResponse<DepositReceiptResponse>>(`/api/wallet/deposit/${referenceNumber}/receipt`, data),

  /**
   * Cancel a pending deposit
   */
  cancelDeposit: (referenceNumber: string) =>
    api.post<ApiResponse<{ message: string }>>(`/api/wallet/deposit/${referenceNumber}/cancel`),

  /**
   * Initiate a withdrawal
   */
  initiateWithdrawal: (data: WithdrawalRequest) =>
    api.post<ApiResponse<WithdrawalInitResponse>>('/api/wallet/withdrawal', data),
};

// =============================================================================
// Admin API (Admin Bot - /admin routes)
// =============================================================================

export const adminAPI = {
  // -------------------------------------------------------------------------
  // Dashboard
  // -------------------------------------------------------------------------
  getDashboard: () =>
    api.get<ApiResponse<DashboardResponse>>('/api/admin/dashboard'),

  // -------------------------------------------------------------------------
  // Users
  // -------------------------------------------------------------------------
  getUsers: (params?: { q?: string }) =>
    api.get<ApiResponse<UsersListResponse>>('/api/admin/users', { params }),

  getUserDetails: (telegramId: string) =>
    api.get<ApiResponse<UserDetailResponse>>(`/api/admin/users/${telegramId}`),

  getUserTransactions: (telegramId: string, params?: { year?: number; month?: number }) =>
    api.get<ApiResponse<UserTransactionsResponse>>(`/api/admin/users/${telegramId}/transactions`, { params }),

  banUser: (telegramId: string, reason: string) =>
    api.post<ApiResponse<{ message: string }>>(`/api/admin/users/${telegramId}/ban`, { reason }),

  unbanUser: (telegramId: string, reason?: string) =>
    api.post<ApiResponse<{ message: string }>>(`/api/admin/users/${telegramId}/unban`, { reason }),

  recreateUser: (telegramId: string, reason: string) =>
    api.post<ApiResponse<RecreateUserResponse>>(`/api/admin/users/${telegramId}/recreate`, { reason }),

  // -------------------------------------------------------------------------
  // Deposits
  // -------------------------------------------------------------------------
  getDeposits: (params?: { filter?: string; limit?: number; search?: string }) =>
    api.get<ApiResponse<DepositsListResponse>>('/api/admin/deposits', { params }),

  getDepositDetails: (transactionId: string) =>
    api.get<ApiResponse<DepositDetailResponse>>(`/api/admin/deposits/${transactionId}`),

  forceCompleteDeposit: (transactionId: string, data: ForceCompleteRequest) =>
    api.post<ApiResponse<ForceCompleteResponse>>(`/api/admin/deposits/${transactionId}/force-complete`, data),

  syncWithProvider: (transactionId: string) =>
    api.post<ApiResponse<SyncDepositResponse>>(`/api/admin/deposits/${transactionId}/sync`),

  editDeposit: (referenceNumber: string, data: { usdtAmount?: number; exchangeRate?: number; commission?: number; note?: string }) =>
    api.put<ApiResponse<EditDepositResponseData>>(`/api/admin/deposits/${referenceNumber}/edit`, data),

  // -------------------------------------------------------------------------
  // Withdrawals
  // -------------------------------------------------------------------------
  getWithdrawals: (params?: { limit?: number; search?: string }) =>
    api.get<ApiResponse<WithdrawalsListResponse>>('/api/admin/withdrawals', { params }),

  getWithdrawalDetails: (referenceNumber: string) =>
    api.get<ApiResponse<AdminWithdrawalDetailResponseData>>(`/api/admin/withdrawals/${referenceNumber}`),

  resendWithdrawal: (transactionId: string, data: { adminNote: string }) =>
    api.post<ApiResponse<ResendWithdrawalResponse>>(`/api/admin/withdrawals/${transactionId}/resend`, data),

  editWithdrawal: (referenceNumber: string, data: { usdtAmount?: number; exchangeRate?: number; commission?: number; note?: string }) =>
    api.put<ApiResponse<EditWithdrawalResponseData>>(`/api/admin/withdrawals/${referenceNumber}/edit`, data),

  // -------------------------------------------------------------------------
  // Reports
  // -------------------------------------------------------------------------
  getTurnoverReport: (period: 'weekly' | 'monthly' | 'yearly') =>
    api.get<ApiResponse<TurnoverReportResponse>>('/api/admin/reports/turnover', { params: { period } }),

  // -------------------------------------------------------------------------
  // Cascade Configuration
  // -------------------------------------------------------------------------
  getDepositCascade: () =>
    api.get<ApiResponse<CascadeConfigResponse>>('/api/admin/cascade/deposit'),

  toggleDepositCascade: (enabled: boolean) =>
    api.post<ApiResponse<{ message: string }>>('/api/admin/cascade/deposit/toggle', { enabled }),

  swapDepositPositions: (data: SwapPositionsRequest) =>
    api.post<ApiResponse<{ message: string }>>('/api/admin/cascade/deposit/swap', data),

  reorderDepositCascade: (data: ReorderCascadeRequest) =>
    api.post<ApiResponse<{ message: string }>>('/api/admin/cascade/deposit/reorder', data),

  dropFromDepositCascade: (data: DropFromCascadeRequest) =>
    api.post<ApiResponse<{ message: string }>>('/api/admin/cascade/deposit/drop', data),

  addToDepositCascade: (data: AddToCascadeRequest) =>
    api.post<ApiResponse<{ message: string }>>('/api/admin/cascade/deposit/add', data),

  getDisabledDepositMethods: () =>
    api.get<ApiResponse<DisabledMethodsResponse>>('/api/admin/cascade/deposit/disabled'),

  toggleDepositMethodStatus: (hash: string, enabled: boolean) =>
    api.post<ApiResponse<{ hash: string; enabled: boolean; position: number; message: string }>>(
      `/api/admin/cascade/deposit/${hash}/toggle`,
      { enabled }
    ),

  // Withdrawal cascade
  // NOTE: There is no separate withdrawal cascade toggle endpoint.
  // The deposit cascade toggle (toggleDepositCascade) toggles the ENTIRE cascade.
  getWithdrawalCascade: () =>
    api.get<ApiResponse<CascadeConfigResponse>>('/api/admin/cascade/withdrawal'),

  swapWithdrawalPositions: (data: SwapPositionsRequest) =>
    api.post<ApiResponse<{ message: string }>>('/api/admin/cascade/withdrawal/swap', data),

  reorderWithdrawalCascade: (data: ReorderCascadeRequest) =>
    api.post<ApiResponse<{ message: string }>>('/api/admin/cascade/withdrawal/reorder', data),

  dropFromWithdrawalCascade: (data: DropFromCascadeRequest) =>
    api.post<ApiResponse<{ message: string }>>('/api/admin/cascade/withdrawal/drop', data),

  addToWithdrawalCascade: (data: AddToCascadeRequest) =>
    api.post<ApiResponse<{ message: string }>>('/api/admin/cascade/withdrawal/add', data),

  getDisabledWithdrawalMethods: () =>
    api.get<ApiResponse<DisabledMethodsResponse>>('/api/admin/cascade/withdrawal/disabled'),

  toggleWithdrawalMethodStatus: (hash: string, enabled: boolean) =>
    api.post<ApiResponse<{ hash: string; enabled: boolean; position: number; message: string }>>(
      `/api/admin/cascade/withdrawal/${hash}/toggle`,
      { enabled }
    ),

  // -------------------------------------------------------------------------
  // Bonus Configuration
  // -------------------------------------------------------------------------
  getBonusConfig: () =>
    api.get<ApiResponse<BonusConfigResponse>>('/api/admin/bonus'),

  updateBonusPercentage: (data: BonusUpdateRequest) =>
    api.post<ApiResponse<{ message: string }>>('/api/admin/bonus', data),

  // -------------------------------------------------------------------------
  // Referees
  // -------------------------------------------------------------------------
  getReferees: () =>
    api.get<ApiResponse<RefereeListItem[]>>('/api/admin/referees'),

  // -------------------------------------------------------------------------
  // Limits Configuration
  // -------------------------------------------------------------------------
  getLimits: () =>
    api.get<ApiResponse<LimitsResponse>>('/api/admin/limits'),

  updateDepositMethodLimits: (method: string, data: UpdateLimitsRequest) =>
    api.put<ApiResponse<MethodLimits>>(`/api/admin/limits/deposit/${method}`, data),

  updateWithdrawalMethodLimits: (method: string, data: UpdateLimitsRequest) =>
    api.put<ApiResponse<MethodLimits>>(`/api/admin/limits/withdrawal/${method}`, data),
};

// =============================================================================
// Worker API (Withdrawal Bot - /worker routes)
// =============================================================================

export const workerAPI = {
  /**
   * Get all pending withdrawals
   */
  getPendingWithdrawals: () =>
    api.get<ApiResponse<PendingWithdrawalsResponse>>('/api/worker/withdrawals'),

  /**
   * Get withdrawal details
   */
  getWithdrawalDetails: (transactionId: string) =>
    api.get<ApiResponse<WithdrawalDetailResponse>>(`/api/worker/withdrawals/${transactionId}`),

  /**
   * Lock withdrawal for processing
   */
  lockWithdrawal: (transactionId: string) =>
    api.post<ApiResponse<LockWithdrawalResponse>>(`/api/worker/withdrawals/${transactionId}/lock`),

  /**
   * Unlock withdrawal
   */
  unlockWithdrawal: (transactionId: string) =>
    api.post<ApiResponse<{ message: string }>>(`/api/worker/withdrawals/${transactionId}/unlock`),

  /**
   * Process withdrawal via auto-cascade
   */
  autoCascade: (transactionId: string) =>
    api.post<ApiResponse<AutoCascadeResponse>>(`/api/worker/withdrawals/${transactionId}/auto-cascade`),

  /**
   * Complete withdrawal manually with proof
   */
  manualComplete: (transactionId: string, data: ManualCompleteRequest) =>
    api.post<ApiResponse<ManualCompleteResponse>>(`/api/worker/withdrawals/${transactionId}/manual`, data),

  /**
   * Decline withdrawal (NO REFUND - confiscation)
   */
  declineWithdrawal: (transactionId: string, data: DeclineRequest) =>
    api.post<ApiResponse<DeclineWithdrawalResponse>>(`/api/worker/withdrawals/${transactionId}/decline`, data),

  /**
   * Edit withdrawal amount (reduce payout)
   */
  editAmount: (transactionId: string, data: EditAmountRequest) =>
    api.post<ApiResponse<EditAmountResponse>>(`/api/worker/withdrawals/${transactionId}/edit-amount`, data),

  /**
   * Confirm completion (paid -> completed)
   */
  confirmComplete: (transactionId: string) =>
    api.post<ApiResponse<ConfirmCompleteResponse>>(`/api/worker/withdrawals/${transactionId}/confirm-complete`),
};

// =============================================================================
// Referee API (Referee Bot - /referee routes)
// =============================================================================

export const refereeAPI = {
  /**
   * Get referee profile
   */
  getProfile: () =>
    api.get<ApiResponse<RefereeProfileResponse>>('/api/referee/profile'),

  /**
   * Get balance log (commission history)
   */
  getBalanceLog: (params?: { limit?: number; offset?: number }) =>
    api.get<ApiResponse<RefereeBalanceLogResponse>>('/api/referee/balance-log', { params }),

  /**
   * Get referee withdrawals
   */
  getWithdrawals: (params?: { limit?: number; offset?: number }) =>
    api.get<ApiResponse<RefereeWithdrawalsResponse>>('/api/referee/withdrawals', { params }),

  /**
   * Create withdrawal request
   */
  createWithdrawal: (data: RefereeWithdrawalRequest) =>
    api.post<ApiResponse<CreateRefereeWithdrawalResponse>>('/api/referee/withdrawal', data),

  /**
   * Create token conversion (credit to any user's game account)
   */
  createTokenConversion: (data: TokenConversionRequest) =>
    api.post<ApiResponse<TokenConversionResponse>>('/api/referee/token-conversion', data),

  /**
   * Get conversion history
   */
  getConversions: (params?: { limit?: number; offset?: number }) =>
    api.get<ApiResponse<RefereeConversionsResponse>>('/api/referee/conversions', { params }),
};

// =============================================================================
// Super Admin API (Super Admin - /superadmin routes)
// =============================================================================

export interface SuperAdminStaffMember {
  identifier: string;
  role: 'super_admin' | 'admin' | 'worker' | 'referee';
  active: boolean;
  createdAt: string;
  lastActiveDate: string | null;
  name?: string | null;
  referralCode?: string | null;
  balance?: number | null;
}

export interface SuperAdminListStaffResponse {
  staff: SuperAdminStaffMember[];
  total: number;
}

export interface SuperAdminCreateStaffResponse {
  identifier: string;
  role: string;
  referralCode?: string;
  message: string;
}

export interface SuperAdminToggleStaffResponse {
  identifier: string;
  active: boolean;
  message: string;
}

export const superadminAPI = {
  /**
   * List all staff members
   */
  listStaff: () =>
    api.get<ApiResponse<SuperAdminListStaffResponse>>('/api/superadmin/staff'),

  /**
   * Create a staff member
   */
  createStaff: (data: {
    telegramId: string;
    role: 'admin' | 'worker' | 'referee';
    name?: string;
    referralCode?: string;
  }) =>
    api.post<ApiResponse<SuperAdminCreateStaffResponse>>('/api/superadmin/staff', data),

  /**
   * Deactivate a staff member
   */
  deactivateStaff: (identifier: string) =>
    api.post<ApiResponse<SuperAdminToggleStaffResponse>>(
      `/api/superadmin/staff/${identifier}/deactivate`
    ),

  /**
   * Reactivate a staff member
   */
  reactivateStaff: (identifier: string) =>
    api.post<ApiResponse<SuperAdminToggleStaffResponse>>(
      `/api/superadmin/staff/${identifier}/reactivate`
    ),
};

// =============================================================================
// Media Service API (Port 30003)
// =============================================================================

const MEDIA_SERVICE_URL = import.meta.env.VITE_MEDIA_SERVICE_URL || 'http://localhost:30003';

export const mediaAPI = {
  /**
   * Upload photo
   * Uses Zod contract: UploadSuccessResponse from @club-bot/shared/contracts
   */
  uploadPhoto: async (
    file: File,
    type: MediaUploadType,
    transactionId?: string
  ): Promise<UploadSuccessResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    if (transactionId) {
      formData.append('transactionId', transactionId);
    }

    const response = await axios.post<UploadSuccessResponse>(
      `${MEDIA_SERVICE_URL}/upload`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-Telegram-Init-Data': getTelegramInitData(),
        },
      }
    );

    return response.data;
  },

  /**
   * Get photo URL
   */
  getPhotoUrl: (fileId: string) => `${MEDIA_SERVICE_URL}/media/${fileId}`,
};

// =============================================================================
// Export default instance for custom requests
// =============================================================================

export default api;
